import { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { AxiosError } from 'axios'
import { fetchEmailPreferences, saveEmailPreferences, EmailPreferencesView } from '../../../api'

function extractErrorMessage(err: AxiosError<{ message?: string }>, fallback: string): string {
  return err.response?.data?.message || fallback
}

export function EmailPreferences() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [view, setView] = useState<EmailPreferencesView | null>(null)
  const [optedOut, setOptedOut] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!token) {
      setError('This link is missing its preferences token.'.toTranslatedDisplay())
      setLoading(false)
      return
    }
    fetchEmailPreferences(token)
      .then((data) => {
        setView(data)
        setOptedOut(new Set(data.optionalTypes.filter(t => t.optedOut).map(t => t.type)))
      })
      .catch((err: AxiosError<{ message?: string }>) => {
        setError(extractErrorMessage(err, 'This link is invalid or has expired.'.toTranslatedDisplay()))
      })
      .finally(() => setLoading(false))
  }, [token])

  const toggle = (type: string) => {
    setSaved(false)
    setOptedOut(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const data = await saveEmailPreferences(token, Array.from(optedOut))
      setView(data)
      setSaved(true)
    } catch (err) {
      setError(extractErrorMessage(err as AxiosError<{ message?: string }>, 'Failed to save your preferences. Please try again.'.toTranslatedDisplay()))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='text-center mb-10'>
        <span className='spinner-border spinner-border-sm align-middle me-2'></span>
        {'Loading your email preferences...'.toTranslatedDisplay()}
      </div>
    )
  }

  if (!view) {
    return (
      <div className='text-center mb-10'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{'Email Preferences'.toTranslatedDisplay()}</h1>
        <div className='mb-10 bg-light-danger p-8 rounded'>
          <div className='text-danger fw-bold'>{error}</div>
        </div>
        <Link to='/'><button className='btn btn-light'>{'Go to homepage'.toTranslatedDisplay()}</button></Link>
      </div>
    )
  }

  return (
    <div className='mb-10'>
      <div className='text-center mb-8'>
        <h1 className='text-gray-900 fw-bolder mb-3'>{'Email Preferences'.toTranslatedDisplay()}</h1>
        <div className='text-gray-500 fw-semibold fs-6'>{view.email}</div>
      </div>

      {error && (
        <div className='mb-8 bg-light-danger p-6 rounded'>
          <div className='text-danger fw-bold'>{error}</div>
        </div>
      )}

      {saved && (
        <div className='mb-8 bg-light-success p-6 rounded'>
          <div className='text-success fw-bold'>
            <i className='bi bi-check-circle me-2'></i>
            {'Your preferences have been saved.'.toTranslatedDisplay()}
          </div>
        </div>
      )}

      <div className='mb-8'>
        {view.optionalTypes.map((info) => {
          const isHighlighted = info.type === view.highlightType
          const isOptedOut = optedOut.has(info.type)
          return (
            <div
              key={info.type}
              className={`d-flex align-items-center justify-content-between p-4 mb-3 rounded ${isHighlighted ? 'bg-light-primary border border-primary' : 'bg-light'}`}
            >
              <div className='me-4'>
                <div className='fw-bold text-gray-900'>
                  {info.label}
                  {isHighlighted && (
                    <span className='badge badge-light-primary ms-2'>{'This email'.toTranslatedDisplay()}</span>
                  )}
                </div>
                <div className='text-muted fs-7'>{info.description}</div>
              </div>
              <div className='form-check form-switch flex-shrink-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  checked={!isOptedOut}
                  onChange={() => toggle(info.type)}
                  aria-label={info.label}
                />
              </div>
            </div>
          )
        })}
      </div>

      <div className='mb-8'>
        <div className='fw-bold text-gray-700 mb-3'>{'Always sent (cannot be turned off)'.toTranslatedDisplay()}</div>
        {view.mandatoryCategories.map((category) => (
          <div key={category} className='d-flex align-items-center justify-content-between p-4 mb-2 rounded bg-light opacity-50'>
            <div className='text-gray-700'>{category}</div>
            <div className='form-check form-switch flex-shrink-0'>
              <input className='form-check-input' type='checkbox' role='switch' checked disabled />
            </div>
          </div>
        ))}
        <div className='text-muted fs-7 mt-2'>
          {'These are required for account security and billing, and can only stop by deleting your account.'.toTranslatedDisplay()}
        </div>
      </div>

      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='button'
          className='btn btn-primary'
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? (
            <span className='indicator-progress'>
              {'Saving...'.toTranslatedDisplay()}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          ) : (
            'Save Preferences'.toTranslatedDisplay()
          )}
        </button>
      </div>
    </div>
  )
}
